<script setup lang="ts">
import MarketingFlowTriggerTypeBaseWrap from './BaseWrap.vue'
import { resolveAudienceScopedStoreIds } from '@/utils/audienceStoreIds'
import useAudienceStoreScope from '@/composables/useAudienceStoreScope'

import type { Audience } from '@/types/types'
import type { TriggerSettings } from '../../types/marketingFlow'

const props = defineProps<{
  audiences: Audience[]
}>()

const { t } = useI18n()
const { stores } = useLocalStorage()

const triggerSettings = defineModel<TriggerSettings>('triggerSettings')

// A SEGMENT_ENTRY/EXIT flow only fires for members of its trigger audience, so its stores
// must stay within that audience's scope too, on top of the included audiences filter.
const triggerAudienceScope = computed(() => {
  const { triggerType, selectedAudience } = triggerSettings.value
  if (triggerType !== 'SEGMENT_ENTRY' && triggerType !== 'SEGMENT_EXIT') return null
  const triggerAudience = props.audiences.find((audience) => audience.id === selectedAudience)
  return triggerAudience ? resolveAudienceScopedStoreIds([triggerAudience]) : null
})
const includedAudiencesScope = computed(() =>
  resolveAudienceScopedStoreIds(triggerSettings.value.includedSegments ?? [])
)
const scopedStoreIds = computed(() => {
  const triggerScope = triggerAudienceScope.value
  const includedScope = includedAudiencesScope.value
  if (!triggerScope || !includedScope) return triggerScope ?? includedScope
  const overlap = triggerScope.filter((id) => includedScope.includes(id))
  return overlap.length ? overlap : triggerScope
})

const handleStoreSelection = () => {
  emit('updateStores')
  emit('onChanges', { isCritical: false, field: 'stores', value: triggerSettings.value.selectedStores })
}

const emit = defineEmits<{
  (e: 'updateStores'): void
  (e: 'onChanges', { isCritical, field, value }): void
}>()

useAudienceStoreScope({
  scopedStoreIds,
  getSelectedIds: () => triggerSettings.value.selectedStores ?? [],
  setSelectedIds: (ids) => {
    if (!triggerSettings.value.selectedStores) return
    triggerSettings.value.selectedStores = ids
    handleStoreSelection()
  },
  getRestoreIds: () => stores.value.map((store) => store.id)
})
</script>

<template>
  <MarketingFlowTriggerTypeBaseWrap
    icon="IconoirShop"
    :title="t('pages.marketingFlow.marketingFlowEditPage.trigger.stores')"
  >
    <div class="flex flex-col gap-1.5">
      <label class="text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.favoriteStore') }}
        <span class="text-red-500">*</span>
      </label>
      <MultiSelectStores
        v-model="triggerSettings.selectedStores"
        :has-label="false"
        class="sm:w-full"
        :max-shown-items="3"
        :scoped-store-ids="scopedStoreIds"
        @update:model-value="handleStoreSelection"
      />
    </div>
  </MarketingFlowTriggerTypeBaseWrap>
</template>
